import TicketsResource from 'flow/api/omnitickets';
import Actions from 'flow/actions';
import { networkErrorDispatcher } from 'flow/actions/app-actions';
import {
    selectTicketsSearchedNextPageNumberForAPIRequest,
    selectTicketsSortForApi,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { AppDispatch } from 'flow/types/Redux';
import { State } from 'flow/types/State';
import { Ticket, TicketsSearchParams } from 'flow/types/Tickets';
import { segmentTrack } from 'flow/actions/segment-actions';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';

const TICKETS_PAGE_SIZE = 25;

type SearchContext = 'MY_TICKETS' | 'UNASSIGNED' | 'ALL' | 'ADVANCED_SEARCH';

type TicketsSearchCallback = {
    onResult: (payload: Ticket[]) => void;
    onClientError: (error?: unknown) => void;
    onError: (error?: unknown) => void;
};

// The assignee/state filter that scopes the generic tickets endpoint to each
// tab. Advanced search carries its own filters in ticketsSearchParams.
const getContextFilter = (
    context: SearchContext,
    ticketsSearchParams: TicketsSearchParams,
    ownExtensionKey: string,
): Partial<TicketsSearchParams> => {
    if (context === 'MY_TICKETS')
        return { assignee: ownExtensionKey, state: 'open' };
    if (context === 'UNASSIGNED')
        return { assignee: 'unassigned', state: 'open' };
    if (context === 'ADVANCED_SEARCH') return ticketsSearchParams;
    return {}; // ALL
};

const callTicketsApi = (
    state: State,
    context: SearchContext,
    callback: TicketsSearchCallback,
    pageSize: number,
    pageNumber: number,
    searchQuery: string,
    ticketsSearchParams: TicketsSearchParams,
) => {
    const filter = getContextFilter(
        context,
        ticketsSearchParams,
        selectOwnExtensionKey(state),
    );
    const { sortField, sortDirection } = selectTicketsSortForApi(state);
    TicketsResource.getTickets(
        callback,
        pageSize,
        pageNumber,
        searchQuery,
        filter.assignee,
        filter.state,
        filter.since,
        filter.countOnly,
        filter.marked,
        filter.sessionIds,
        filter.tagKeysOr,
        sortField,
        sortDirection,
    );
};

// All searches share one result list in the store. Each new search gets a
// new generation, and a response from an older generation does not touch the
// store. Without this, a slow response from another pill or an older query
// can replace or extend the results of the current search.
let searchGeneration = 0;

const createTicketsSearchCallback = (
    dispatch: AppDispatch,
    resolve: (value: Ticket[]) => void,
    reject: () => void,
    nextPageNumber: number | undefined,
    segmentAction: typeof addSegmentSearchEventLatest,
    contextForSegment: string,
    generation: number,
): TicketsSearchCallback => ({
    onResult(payload: Ticket[]) {
        if (generation !== searchGeneration) {
            resolve(payload);
            return;
        }
        const apiHasMoreData = payload.length === TICKETS_PAGE_SIZE;
        dispatch(
            setTicketsSearchedData(payload, apiHasMoreData, nextPageNumber),
        );
        dispatch(segmentAction(contextForSegment, payload));
        resolve(payload);
    },
    onClientError: (/*error*/) => {
        reject();
    },
    onError: (/*error*/) => {
        if (generation === searchGeneration) networkErrorDispatcher(dispatch)();
        reject();
    },
});

export const executeTicketsSearchLatest =
    (ticketsSearchParams: TicketsSearchParams, context: SearchContext) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const searchQueryFormatted = encodeURIComponent(
            ticketsSearchParams.searchQuery,
        );
        const contextForSegment = `${context} search bar`;
        searchGeneration += 1;
        const generation = searchGeneration;
        return new Promise((resolve, reject) => {
            callTicketsApi(
                getState(),
                context,
                createTicketsSearchCallback(
                    dispatch,
                    resolve,
                    reject,
                    undefined,
                    addSegmentSearchEventLatest,
                    contextForSegment,
                    generation,
                ),
                TICKETS_PAGE_SIZE,
                0,
                searchQueryFormatted,
                ticketsSearchParams,
            );
        });
    };

export const executeTicketsSearchNext =
    (ticketsSearchParams: TicketsSearchParams, context: SearchContext) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const searchQueryFormatted = encodeURIComponent(
            ticketsSearchParams.searchQuery,
        );
        const pageNumber =
            selectTicketsSearchedNextPageNumberForAPIRequest(getState());
        const contextForSegment = `${context} page ${pageNumber}`;
        return new Promise((resolve, reject) => {
            callTicketsApi(
                getState(),
                context,
                createTicketsSearchCallback(
                    dispatch,
                    resolve,
                    reject,
                    pageNumber + 1,
                    addSegmentSearchEventNext,
                    contextForSegment,
                    searchGeneration,
                ),
                TICKETS_PAGE_SIZE,
                pageNumber,
                searchQueryFormatted,
                ticketsSearchParams,
            );
        });
    };

const setTicketsSearchedData =
    (data: Ticket[], apiHasMoreData: boolean, nextPageNumber?: number) =>
    (dispatch: AppDispatch) => {
        dispatch({
            type: Actions.SET_TICKETS_SEARCHED_DATA,
            data,
            nextPageNumber,
            apiHasMoreData,
        });
    };

const addSegmentSearchEventLatest =
    (context: string, payload: Ticket[]) => (dispatch: AppDispatch) => {
        dispatch(
            segmentTrack(TICKET_STRINGS.SEARCH_PERFORMED, {
                component: context,
                fruitful: payload.length > 0,
            }),
        );
    };

const addSegmentSearchEventNext =
    (context: string, payload: Ticket[]) => (dispatch: AppDispatch) => {
        dispatch(
            segmentTrack(TICKET_STRINGS.SEARCH_LOAD_MORE, {
                component: context,
                fruitful: payload.length > 0,
            }),
        );
    };
